const LOCATION_KEY = "personalizedLocation";

// How a stored location was obtained. Only SOURCE_IP is an ambient guess; every other value
// is something the user actively did and must not be silently replaced.
// This lives inside the stored JSON rather than in a sibling key so it can never desync from
// the value it describes, and so it survives the move from sessionStorage to cookie.
// The FAD app reads these same values - keep them in sync with support/utils.js there.
const SOURCE_IP = "ip";           // inferred from the request IP, no user input
const SOURCE_BROWSER = "browser"; // navigator.geolocation, user granted permission
const SOURCE_MANUAL = "manual";   // typed into the modal with "save" unchecked
const SOURCE_PROFILE = "profile"; // typed into the modal with "save" checked (cookie + SFMC)

function stampSource(locationData, source) {
    if (locationData) locationData.source = source;
    return locationData;
}

function parseLocation(raw, fallbackSource) {
    if (!raw) return null;
    try {
        const locationData = JSON.parse(raw);
        // Values written before `source` existed take the caller's fallback.
        if (locationData && !locationData.source) locationData.source = fallbackSource;
        return locationData;
    } catch (error) {
        console.error("Error parsing stored location:", error);
        return null;
    }
}

// Keeps the existing dataLayer values so no GTM changes are needed.
function locationSourceLabel(source) {
    if (source === SOURCE_PROFILE) return "SFMC Profile";
    if (source === SOURCE_BROWSER) return "Browser";
    return "Session Storage";
}

function getLocationZipCookie(name) {
    const cookie = document.cookie.split('; ').find(row => row.startsWith(name + '='));
    return cookie ? decodeURIComponent(cookie.split('=')[1]) : null;
}

function setLocationZipCookie(name, value, days) {
    const expires = new Date();
    expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
    //const serializedValue = JSON.stringify(value);
    document.cookie = `${name}=${encodeURIComponent(value)};expires=${expires.toUTCString()};path=/`;
}

function setLocationButtonLabel(el, locationText) {
    el.setAttribute("aria-label", locationText ? `Location: ${locationText}` : "Set your location");
}

function updateLocationButton(city, zip) {
    const locationModal = document.querySelector(".js-location-modal");
    if (locationModal) {
        if (city != "" || zip != "") {
            const locationText = zip != '' ? (city != "" ? `${city} (${zip})` : `${zip}`) : `${city}`;
            locationModal.textContent = locationText;
            setLocationButtonLabel(locationModal, locationText);
        }
    }
}

function fetchLocationFromIP(callback) {
    fetch("/api/personalization/GetIPLocation")
        .then(response => {
            if (!response.ok || response.status === 204) return null;
            return response.json();
        })
        .then(locationData => {
            if (!locationData) return;
            stampSource(locationData, SOURCE_IP);
            updateLocationButton(locationData.city, locationData.zip);
            if (callback) callback(locationData);
        })
        .catch(error => console.error("Error fetching IP-based location:", error));
}

function reverseGeocodeLatLng(lat, lng, callback) {
    fetchFreshpaint("reverse-geocode", { QueryPosition: [lng, lat], MaxResults: 1 }, callback);
}

function reverseGeocodeZip(zip, callback) {
    fetchFreshpaint("geocode", { QueryText: zip, MaxResults: 1, Filter: { IncludeCountries: ["USA"] } }, callback);
}

function fetchFreshpaint(endpoint, body, callback) {
    fetch("https://freshpaint-hipaa-maps.com/v2/" + endpoint + "?token=" + freshpaintEnvironmentID, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body)
    })
        .then(response => response.json())
        .then(data => {
            const result = data && data.ResultItems && data.ResultItems[0];
            if (!result || !result.Address || !result.Position) {
                console.error("Freshpaint " + endpoint + " returned no usable results");
                if (callback) callback(null);
                return;
            }
            const locationData = mapFreshpaintResult(result);
            updateLocationButton(locationData.city, locationData.zip);
            if (callback) callback(locationData);
        })
        .catch(error => {
            console.error("Error calling Freshpaint " + endpoint + ":", error);
            if (callback) callback(null);
        });
}

function mapFreshpaintResult(result) {
    const addr = result.Address || {};
    return {
        city: addr.Locality || "",
        // Freshpaint may return ZIP+4 ("94043-3201"); the rest of the app expects 5-digit
        zip: (addr.PostalCode || "").split("-")[0],
        state: (addr.Region && addr.Region.Code) || "",
        // Freshpaint Position is [Lng, Lat]
        lat: result.Position[1],
        lng: result.Position[0]
    };
}

function determineLocation(callback, promptIfNeeded) {
    const locationModalError = document.querySelector(".js-modal-error");
    navigator.permissions.query({ name: "geolocation" }).then(permissionStatus => {
        // "granted" always uses real browser geolocation. 
        // The "prompt" state only does so when promptIfNeeded is set (the modal's "Use precise location" button)
        // getCurrentPosition triggers the browser permission prompt. 
        // On initial page load promptIfNeeded is omitted so we silently fall back to IP
        if (permissionStatus.state === "granted" || (promptIfNeeded && permissionStatus.state === "prompt")) {
            if (locationModalError) locationModalError.style.display = 'none';
            navigator.geolocation.getCurrentPosition(
                position => reverseGeocodeLatLng(
                    position.coords.latitude,
                    position.coords.longitude,
                    locationData => { if (callback) callback(stampSource(locationData, SOURCE_BROWSER)); }
                ),
                error => {
                    console.error("Geolocation error:", error);
                    fetchLocationFromIP(callback);
                }
            );
        } else if (permissionStatus.state === "denied") {
            fetchLocationFromIP(callback);
            if (locationModalError) locationModalError.style.display = 'block';
        } else {
            fetchLocationFromIP(callback);
        }
    });
}

function getUserLocation() {
    // Session first, then the saved cookie. Anything already in session was written by this
    // script or by the FAD app, so it carries its own source; a cookie can only have come
    // from the modal's "save" checkbox.
    let locationData = parseLocation(sessionStorage.getItem(LOCATION_KEY), SOURCE_IP);
    if (!locationData) {
        locationData = parseLocation(getLocationZipCookie(LOCATION_KEY), SOURCE_PROFILE);
    }

    if (locationData) {
        // Derive the analytics label from provenance instead of overwriting it on every
        // page load, which used to clobber 'Browser' set by the FAD app.
        sessionStorage.setItem('locationSetBy', locationSourceLabel(locationData.source));
        $(".js-location-desktop").show();
        updateLocationButton(locationData.city, locationData.zip);
        return;
    }

    determineLocation(locationData => {
        if (!locationData) return;
        sessionStorage.setItem(LOCATION_KEY, JSON.stringify(locationData));
        sessionStorage.setItem('locationSetBy', locationSourceLabel(locationData.source));
        $(".js-location-desktop").show();
    });
}

function getSalesforceUserId() {
    var cookie = (document.cookie.split(';')
        .filter(function (c) {
            return c.trim().indexOf('_evga_') === 0;
        }))[0];
    return decodeURIComponent(cookie).split('=')[1].split(':')[1].split('"')[1];
}

function UpdateSFMCUserProfile(zipcode) {

    var parameter = {
        data: `{
    "action": "Update Profile",
    "itemAction": "View Item",
    "user": {
        "id": "${getSalesforceUserId()}",
        "attributes": {
         "preferredZipcode": "${zipcode}"
        }
    },
    "source": {
        "channel": "Server"
    }
}`
    }

    $.ajax({
        type: "POST",
        contentType: "application/json; charset=utf-8",
        url: "/api/personalization/UpdateUserProfile",
        data: JSON.stringify(parameter),
        dataType: "json",
        async: true,
        success: function (response) {
            console.log(response);
        },
        error: function (xhr, ajaxOptions, thrownError) {
            console.log("fail");
            console.log(xhr.status);
            console.log(thrownError);
            console.log(xhr.responseText);
        }
    });

}

var nbaZipCodeControlGroupCookieName = "nba-Recent-LocationZipControl-Group";
var nbaZipCodeControlGroupAllowed = getLocationZipCookie(nbaZipCodeControlGroupCookieName);
if (nbaZipCodeControlGroupAllowed == null || nbaZipCodeControlGroupAllowed == "") {
    if (Math.random() < zipcodeControlGroupAllowedPercentage) {
        nbaZipCodeControlGroupAllowed = "y";
    }
    else {
        nbaZipCodeControlGroupAllowed = "n";
    }
    setLocationZipCookie(nbaZipCodeControlGroupCookieName, nbaZipCodeControlGroupAllowed, 365);
}

if (nbaZipCodeControlGroupAllowed == "y") {

    const updateLocationButtonEl = document.querySelector(".js-location-update");
    const updateLocationField = document.querySelector(".js-location-zip");
    const allowLocationSave = document.querySelector(".js-location-check");
    const modalPreciseLocation = document.querySelector(".js-modal-precise");

    if (updateLocationButtonEl) {
        updateLocationButtonEl.addEventListener("click", () => {
            const zip = updateLocationField.value;
            if (zip) {
                reverseGeocodeZip(zip, locationData => {
                    if (!locationData) return;
                    window.dataLayer = window.dataLayer || [];
                    if (allowLocationSave.checked) {
                        stampSource(locationData, SOURCE_PROFILE);
                        setLocationZipCookie(LOCATION_KEY, JSON.stringify(locationData), 1);
                        sessionStorage.removeItem(LOCATION_KEY);
                        sessionStorage.setItem('locationSetBy', locationSourceLabel(SOURCE_PROFILE));
                        UpdateSFMCUserProfile(locationData.zip);
                        MicroModal.close('location-modal');

                        window.dataLayer.push({
                            'event': 'location_modal_update',
                            'location_saved': 'true'
                        });
                    } else {
                        stampSource(locationData, SOURCE_MANUAL);
                        sessionStorage.setItem(LOCATION_KEY, JSON.stringify(locationData));
                        sessionStorage.setItem('locationSetBy', locationSourceLabel(SOURCE_MANUAL));
                        MicroModal.close('location-modal');

                        window.dataLayer.push({
                            'event': 'location_modal_update',
                            'location_saved': 'false'
                        });
                    }
                });
            }
        });
    }

    if (modalPreciseLocation) {
        modalPreciseLocation.addEventListener("click", () => {
          // Fills the field only; the user still has to press Update, which re-geocodes and
          // stamps the result as SOURCE_MANUAL or SOURCE_PROFILE.
          determineLocation(locationData => {
            if (!locationData) return;
            updateLocationField.value = locationData.zip || locationData.city;
          }, true);
        });
      }

    const locationNavButton = document.querySelector(".js-location-modal");
    if (locationNavButton && !locationNavButton.hasAttribute("aria-label")) {
        setLocationButtonLabel(locationNavButton, "");
    }

    getUserLocation();

    document.addEventListener('DOMContentLoaded', () => {
        const locationNavItem = document.querySelector('.js-location-nav-item');
        const mobileLocationContainer = document.querySelector('.js-mobile-location');
        const locationModal = document.getElementById('location-modal');
        const globalHeaderMenus = document.querySelector('.global-header__menus');
        const systemMenuDropdown = document.querySelector('.system-menu__dropdown');

        function moveLocationNavItem() {
            if (window.innerWidth < 900) {
                if (locationNavItem && mobileLocationContainer && !mobileLocationContainer.contains(locationNavItem)) {
                    mobileLocationContainer.appendChild(locationNavItem);
                }
                if (locationModal && systemMenuDropdown && !systemMenuDropdown.contains(locationModal)) {
                    systemMenuDropdown.appendChild(locationModal);
                }
            } else {
                const originalParent = document.querySelector('.js-location-desktop');
                if (locationNavItem && originalParent && !originalParent.contains(locationNavItem)) {
                    originalParent.appendChild(locationNavItem);
                }
                if (locationModal && globalHeaderMenus && !globalHeaderMenus.contains(locationModal)) {
                    globalHeaderMenus.appendChild(locationModal);
                }
            }
        }

        moveLocationNavItem();

        window.addEventListener('resize', moveLocationNavItem);
    });
}
else {
    $(".js-location-desktop").hide();
}